import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { DataGrid } from './datagrid/ResponsiveDataGrid';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import client from '../../lib/api/client';
import EmpModal from './form/modal/EmpModal';

const AUTH_FILTER = {
  blcCd: 'AUTH',
  mlcCd: 'USR',
};

const isUseEnabled = (item) =>
  item?.useYn === undefined || item?.useYn === 'Y' || item?.useYn === true;

const getAuthType = (item) => item?.authTyp || item?.revVal2 || item?.itemNm;

const getAuthName = (item) =>
  item?.authNm || item?.revVal1 || item?.itemNm || item?.authTyp;

// 2026-10-20 수정: COM_COMMON_INF 레코드(권한 코드 자체)에는 tgtTyp 필드가 없는데,
// item.slcCd(권한 코드의 분류값, 예: 'FORECAST'/'EXPENSE')를 fallback으로 썼던 건 버그였다
// — 실제 HR_USR_AUTH에 TGT_TYP='FORECAST' 같은 값이 그대로 저장돼 ActionPermissionService의
// TGT_TYP='USER' 매치가 깨지고(권한이 저장된 것처럼 보이지만 실제로는 조회가 안 됨), 그 결과
// 다른 권한(예: FORECAST_COPY)까지 cngUsrAuth의 전체교체에 휩쓸려 사실상 사라졌었다(로컬
// docker 검증 중 재현·확인). 이 패널은 항상 사용자(USER) 대상 저장만 다루므로 'USER'로 고정한다.
const getTargetType = (item) => item?.tgtTyp || 'USER';

const toList = (data) => {
  if (Array.isArray(data?.list)) {
    return data.list;
  }
  if (Array.isArray(data)) {
    return data;
  }
  return [];
};

const sortAuthCodes = (list) =>
  [...list].sort((a, b) => {
    const stepA = Number(a?.step ?? 0);
    const stepB = Number(b?.step ?? 0);

    if (stepA !== stepB) {
      return stepA - stepB;
    }

    return `${getAuthName(a) || ''}`.localeCompare(`${getAuthName(b) || ''}`);
  });

const AuthStatusPanel = ({ viewType }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { baseCodes, authUser } = useSelector(({ auth }) => ({
    baseCodes: auth.baseCodes,
    authUser: auth.user,
  }));

  const currentUser = useMemo(() => {
    if (authUser) {
      return authUser;
    }

    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, [authUser]);

  const [authCodes, setAuthCodes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [rows, setRows] = useState([]);
  const [checkedAuthTypes, setCheckedAuthTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [authCodeLoading, setAuthCodeLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [authCodeError, setAuthCodeError] = useState('');
  const [contentError, setContentError] = useState('');
  const [userModalOpen, setUserModalOpen] = useState(false);

  const selectedAuth = useMemo(
    () => authCodes.find((item) => getAuthType(item) === selectedKey) || null,
    [authCodes, selectedKey],
  );

  const selectedEmployee = useMemo(
    () =>
      employees.find((item) => (item.usrUuid || item.id) === selectedKey) ||
      null,
    [employees, selectedKey],
  );

  const loadAuthCodes = useCallback(async () => {
    setAuthCodeLoading(true);
    setAuthCodeError('');

    try {
      const filteredBaseCodes = Array.isArray(baseCodes)
        ? baseCodes.filter(
            (item) =>
              item?.blcCd === 'AUTH' &&
              item?.mlcCd === 'USR' &&
              isUseEnabled(item),
          )
        : [];

      if (filteredBaseCodes.length > 0) {
        setAuthCodes(sortAuthCodes(filteredBaseCodes));
        return;
      }

      const res = await client.get('/api/v1/common/list', {
        params: AUTH_FILTER,
      });

      const codeList = sortAuthCodes(toList(res.data).filter(isUseEnabled));
      setAuthCodes(codeList);
    } catch {
      setAuthCodes([]);
      setAuthCodeError('권한 목록 조회 실패');
      enqueueSnackbar('권한 목록 조회 실패', { variant: 'error' });
    } finally {
      setAuthCodeLoading(false);
    }
  }, [baseCodes, enqueueSnackbar]);

  const loadEmployees = useCallback(async () => {
    setListLoading(true);
    setListError('');

    try {
      const res = await client.get('/api/v1/user/list');
      const list = toList(res.data).filter(isUseEnabled);
      setEmployees(list);
    } catch {
      setEmployees([]);
      setListError('사원 목록 조회 실패');
      enqueueSnackbar('목록 조회 실패', { variant: 'error' });
    } finally {
      setListLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadAuthCodes();
  }, [loadAuthCodes]);

  useEffect(() => {
    setSelectedKey(null);
    setRows([]);
    setCheckedAuthTypes([]);
    setContentError('');
    setUserModalOpen(false);

    if (viewType === 'byUser') {
      loadEmployees();
      return;
    }

    setEmployees([]);
    setListLoading(false);
    setListError('');
  }, [viewType, loadEmployees]);

  const fetchStatus = useCallback(
    async (key) => {
      setLoading(true);
      setContentError('');

      try {
        const params =
          viewType === 'byAuth' ? { authTyp: key } : { tgtUuid: key };
        const res = await client.get('/api/v1/common/auth/usr/status', {
          params,
        });
        const list = toList(res.data);

        setRows(
          list.map((item, index) => ({
            id:
              item?.usrUuid ||
              item?.tgtUuid ||
              item?.authUuid ||
              `${getAuthType(item) || 'row'}-${index}`,
            ...item,
          })),
        );

        if (viewType === 'byUser') {
          setCheckedAuthTypes([
            ...new Set(
              list
                .filter((item) => item?.authYn !== 'N')
                .map((item) => getAuthType(item))
                .filter(Boolean),
            ),
          ]);
        }
      } catch {
        setRows([]);
        setCheckedAuthTypes([]);
        setContentError('현황 조회 실패');
        enqueueSnackbar('현황 조회 실패', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [viewType, enqueueSnackbar],
  );

  const handleSelect = useCallback(
    (key) => {
      setSelectedKey(key);
      setRows([]);
      setCheckedAuthTypes([]);
      fetchStatus(key);
    },
    [fetchStatus],
  );

  const refreshSelectedStatus = useCallback(async () => {
    if (!selectedKey) {
      return;
    }

    await fetchStatus(selectedKey);
  }, [fetchStatus, selectedKey]);

  const buildAuthDto = useCallback(
    (authItem, userInfo = {}, overrides = {}) => ({
      authUuid: overrides.authUuid ?? authItem?.authUuid ?? null,
      tgtTyp: overrides.tgtTyp ?? authItem?.tgtTyp ?? getTargetType(authItem),
      tgtUuid:
        overrides.tgtUuid ??
        authItem?.tgtUuid ??
        userInfo?.usrUuid ??
        userInfo?.tgtUuid ??
        null,
      authTyp: overrides.authTyp ?? authItem?.authTyp ?? getAuthType(authItem),
      authNm: overrides.authNm ?? authItem?.authNm ?? getAuthName(authItem),
      authYn: overrides.authYn ?? authItem?.authYn ?? 'Y',
      usrNm: overrides.usrNm ?? authItem?.usrNm ?? userInfo?.usrNm ?? '',
      usrNo: overrides.usrNo ?? authItem?.usrNo ?? userInfo?.usrNo ?? '',
      dptNm: overrides.dptNm ?? authItem?.dptNm ?? userInfo?.dptNm ?? '',
      gradeNm:
        overrides.gradeNm ?? authItem?.gradeNm ?? userInfo?.gradeNm ?? '',
      crtUsr: overrides.crtUsr ?? authItem?.crtUsr ?? currentUser?.usrUuid,
      crtDt: overrides.crtDt ?? authItem?.crtDt ?? null,
      cngUsr: overrides.cngUsr ?? authItem?.cngUsr ?? currentUser?.usrUuid,
      cngDt: overrides.cngDt ?? authItem?.cngDt ?? null,
    }),
    [currentUser],
  );

  const handleDeleteAuth = useCallback(
    async (row) => {
      const usrUuid = row?.usrUuid || row?.tgtUuid;

      if (!usrUuid || !selectedKey) {
        return;
      }

      setActionLoading(true);

      try {
        const res = await client.get('/api/v1/common/auth/usr/status', {
          params: { tgtUuid: usrUuid },
        });

        const remainingAuths = toList(res.data)
          .filter((item) => getAuthType(item) !== selectedKey)
          .map((item) => buildAuthDto(item, row, { tgtUuid: usrUuid }));

        await client.put(
          `/api/v1/common/auth/usr/info/${usrUuid}`,
          remainingAuths,
        );

        enqueueSnackbar('권한을 삭제했습니다.', { variant: 'success' });
        await refreshSelectedStatus();
      } catch {
        enqueueSnackbar('권한 삭제 실패', { variant: 'error' });
      } finally {
        setActionLoading(false);
      }
    },
    [buildAuthDto, enqueueSnackbar, refreshSelectedStatus, selectedKey],
  );

  const handleAddUsers = useCallback(
    async (selectedEmployees) => {
      const employeeList = (
        Array.isArray(selectedEmployees)
          ? selectedEmployees
          : [selectedEmployees]
      ).filter(Boolean);

      if (!selectedAuth || employeeList.length === 0) {
        return;
      }

      const existingUserSet = new Set(
        rows.map((item) => item?.usrUuid || item?.tgtUuid).filter(Boolean),
      );
      const targetUsers = employeeList.filter(
        (item) => !existingUserSet.has(item?.usrUuid),
      );

      if (targetUsers.length === 0) {
        enqueueSnackbar('이미 등록된 사용자입니다.', { variant: 'warning' });
        return;
      }

      setActionLoading(true);

      try {
        await Promise.all(
          targetUsers.map((employee) =>
            client.post(
              '/api/v1/common/auth/usr/info',
              buildAuthDto(selectedAuth, employee, {
                tgtUuid: employee?.usrUuid,
                tgtTyp: getTargetType(selectedAuth),
                authTyp: getAuthType(selectedAuth),
                authNm: getAuthName(selectedAuth),
                authYn: 'Y',
              }),
            ),
          ),
        );

        enqueueSnackbar('사용자를 추가했습니다.', { variant: 'success' });
        await refreshSelectedStatus();
      } catch {
        enqueueSnackbar('사용자 추가 실패', { variant: 'error' });
      } finally {
        setActionLoading(false);
      }
    },
    [buildAuthDto, enqueueSnackbar, refreshSelectedStatus, rows, selectedAuth],
  );

  const handleToggleAuth = useCallback((authTyp, checked) => {
    setCheckedAuthTypes((prev) => {
      if (checked) {
        return [...new Set([...prev, authTyp])];
      }

      return prev.filter((item) => item !== authTyp);
    });
  }, []);

  const handleSaveUserAuths = useCallback(async () => {
    if (!selectedKey || !selectedEmployee) {
      return;
    }

    setActionLoading(true);

    try {
      const payload = authCodes
        .filter((item) => checkedAuthTypes.includes(getAuthType(item)))
        .map((item) =>
          buildAuthDto(item, selectedEmployee, {
            tgtUuid: selectedKey,
            tgtTyp: getTargetType(item),
            authTyp: getAuthType(item),
            authNm: getAuthName(item),
            authYn: 'Y',
          }),
        );

      await client.put(`/api/v1/common/auth/usr/info/${selectedKey}`, payload);

      enqueueSnackbar('권한을 저장했습니다.', { variant: 'success' });
      await refreshSelectedStatus();
    } catch {
      enqueueSnackbar('권한 저장 실패', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  }, [
    authCodes,
    buildAuthDto,
    checkedAuthTypes,
    enqueueSnackbar,
    refreshSelectedStatus,
    selectedEmployee,
    selectedKey,
  ]);

  const columns = useMemo(
    () => [
      { field: 'usrNo', headerName: '사번', width: 110 },
      { field: 'usrNm', headerName: '사원명', width: 110 },
      { field: 'dptNm', headerName: '부서', minWidth: 150, flex: 1 },
      { field: 'gradeNm', headerName: '직급', width: 110 },
      { field: 'crtDt', headerName: '부여일시', minWidth: 180, flex: 1 },
      {
        field: 'actions',
        headerName: '관리',
        width: 110,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            color="error"
            size="small"
            disabled={actionLoading}
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteAuth(params.row);
            }}
          >
            삭제
          </Button>
        ),
      },
    ],
    [actionLoading, handleDeleteAuth],
  );

  const leftList = viewType === 'byAuth' ? authCodes : employees;
  const isLeftLoading = viewType === 'byAuth' ? authCodeLoading : listLoading;
  const leftError = viewType === 'byAuth' ? authCodeError : listError;

  const renderLeftList = () => {
    if (isLeftLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (leftError) {
      return (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography color="error" variant="body2">
            {leftError}
          </Typography>
        </Box>
      );
    }

    if (leftList.length === 0) {
      return (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography color="text.secondary" variant="body2">
            {viewType === 'byAuth'
              ? '조회된 권한이 없습니다.'
              : '조회된 사원이 없습니다.'}
          </Typography>
        </Box>
      );
    }

    return (
      <List sx={{ overflowY: 'auto', flex: 1 }}>
        {leftList.map((item, index) => {
          const key =
            viewType === 'byAuth'
              ? getAuthType(item)
              : item?.usrUuid || item?.id;
          const label =
            viewType === 'byAuth'
              ? getAuthName(item)
              : `${item?.usrNm || ''} (${item?.dptNm || ''})`;

          return (
            <ListItem key={key || index} dense disablePadding>
              <ListItemButton
                selected={selectedKey === key}
                onClick={() => handleSelect(key)}
              >
                <ListItemText
                  primary={<Typography variant="body2">{label}</Typography>}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  const renderByAuthContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (contentError) {
      return (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography color="error" variant="body2">
            {contentError}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableColumnMenu
          disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
          localeText={{
            noRowsLabel: '선택한 권한을 가진 사용자가 없습니다.',
          }}
        />
      </Box>
    );
  };

  const renderByUserContent = () => {
    if (loading || authCodeLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (contentError) {
      return (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography color="error" variant="body2">
            {contentError}
          </Typography>
        </Box>
      );
    }

    if (authCodeError) {
      return (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography color="error" variant="body2">
            {authCodeError}
          </Typography>
        </Box>
      );
    }

    if (authCodes.length === 0) {
      return (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography color="text.secondary" variant="body2">
            사용 가능한 권한 코드가 없습니다.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 2, py: 2, overflowY: 'auto' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedEmployee
            ? `${selectedEmployee.usrNm} ${selectedEmployee.gradeNm || ''} (${selectedEmployee.dptNm || ''})`
            : ''}
        </Typography>

        <FormGroup>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {authCodes.map((item) => {
              const authTyp = getAuthType(item);
              const checked = checkedAuthTypes.includes(authTyp);

              return (
                <Box
                  key={authTyp}
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.3333%',
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        name={authTyp}
                        onChange={(event) =>
                          handleToggleAuth(authTyp, event.target.checked)
                        }
                      />
                    }
                    label={getAuthName(item)}
                  />
                </Box>
              );
            })}
          </Box>
        </FormGroup>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 320px)' }}>
        <Paper
          sx={{
            width: 260,
            flexShrink: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ backgroundColor: '#424242', color: '#fff', py: 1, px: 2 }}>
            <Typography variant="subtitle2">
              {viewType === 'byAuth' ? '권한 목록' : '사원 목록'}
            </Typography>
          </Box>
          <Divider />
          {renderLeftList()}
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!selectedKey ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography color="text.secondary">
                {viewType === 'byAuth'
                  ? '왼쪽에서 권한을 선택하세요'
                  : '왼쪽에서 사원을 선택하세요'}
              </Typography>
            </Box>
          ) : (
            <Paper
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Box>
                  <Typography variant="subtitle2">
                    {viewType === 'byAuth'
                      ? `${getAuthName(selectedAuth)} 사용자`
                      : `${selectedEmployee?.usrNm || ''} 권한 관리`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {viewType === 'byAuth'
                      ? '선택한 권한을 가진 사용자를 관리합니다.'
                      : '체크한 권한으로 사용자의 권한을 저장합니다.'}
                  </Typography>
                </Box>

                {viewType === 'byAuth' ? (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={actionLoading || !selectedAuth}
                    onClick={() => setUserModalOpen(true)}
                  >
                    사용자 추가
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={actionLoading || loading || authCodeLoading}
                    onClick={handleSaveUserAuths}
                  >
                    저장
                  </Button>
                )}
              </Box>

              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {viewType === 'byAuth'
                  ? renderByAuthContent()
                  : renderByUserContent()}
              </Box>
            </Paper>
          )}
        </Box>
      </Box>

      {viewType === 'byAuth' && (
        <EmpModal
          roleTyp=""
          title="사용자 추가"
          multi
          open={userModalOpen}
          handleClose={() => setUserModalOpen(false)}
          selectedEmployee={handleAddUsers}
        />
      )}
    </>
  );
};

export default AuthStatusPanel;
